export interface FormRuleExpressionHelpers {
  fieldHasValue?: (keyName: string, path?: string) => boolean
  resolveField?: (keyName: string, path?: string) => unknown
  resolveCacheProperty?: (
    cacheKey: string,
    formKeyName: string,
    property: string
  ) => unknown
  durationKindFromConsent?: () => string
  ancestorFieldHasValue?: (
    entityName: string,
    keyName: string,
    path?: string
  ) => boolean
  resolveAncestorField?: (
    entityName: string,
    keyName: string,
    path?: string
  ) => unknown
  parentFieldHasValue?: (keyName: string, path?: string) => boolean
  resolveParentField?: (keyName: string, path?: string) => unknown
  resolveAncestorCache?: (
    entityName: string,
    cacheKey: string,
    path: string
  ) => unknown
}

const SINGLE_QUOTED_ARG = "'([^']*)'"
const DOUBLE_QUOTED_ARG = '"([^"]*)"'
const STRING_ARG = `(?:${SINGLE_QUOTED_ARG}|${DOUBLE_QUOTED_ARG})`

const readCapturedString = (
  singleQuoted?: string,
  doubleQuoted?: string
): string => singleQuoted ?? doubleQuoted ?? ''

export const groupByField = (
  items: unknown,
  fieldName: string
): Record<string, unknown[]> => {
  if (!Array.isArray(items)) return {}

  return items.reduce<Record<string, unknown[]>>((acc, item) => {
    if (item == null || typeof item !== 'object') return acc
    const key = (item as Record<string, unknown>)[fieldName]
    if (key == null || key === '') return acc
    const bucketKey = String(key)
    if (!acc[bucketKey]) acc[bucketKey] = []
    acc[bucketKey].push(item)
    return acc
  }, {})
}

const serializeFoldedValue = (value: unknown): string => {
  if (typeof value === 'string') return JSON.stringify(value)
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value)
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  return JSON.stringify(value)
}

const LEGACY_GROUP_REDUCE_PATTERN =
  /data\.content\.reduce\(\s*\(\s*a\s*,\s*x\s*\)\s*=>\s*\(\s*\(\s*a\[x\.name\]\s*\?\?=\s*\[\]\s*\)\.push\(x\)\s*,\s*a\s*\)\s*,\s*\{\s*\}\s*\)/g

const GROUP_BY_NAME_PATTERN = new RegExp(
  `groupByName\\(\\s*data\\.content\\s*,\\s*${STRING_ARG}\\s*\\)`,
  'g'
)

const foldLegacyGroupReduce = (
  expression: string,
  data: Record<string, unknown>
): string =>
  expression.replace(LEGACY_GROUP_REDUCE_PATTERN, () =>
    serializeFoldedValue(groupByField(data['content'], 'name'))
  )

const foldGroupByNameCalls = (
  expression: string,
  data: Record<string, unknown>
): string =>
  expression.replace(GROUP_BY_NAME_PATTERN, (_match, single, double) =>
    serializeFoldedValue(
      groupByField(data['content'], readCapturedString(single, double))
    )
  )

const foldNullaryHelperCalls = (
  expression: string,
  helpers: FormRuleExpressionHelpers
): string => {
  let result = expression

  if (helpers.durationKindFromConsent) {
    const value = serializeFoldedValue(helpers.durationKindFromConsent())
    result = result.replace(/durationKindFromConsent\(\s*\)/g, value)
  }

  return result
}

const foldHelperCall = (
  expression: string,
  pattern: RegExp,
  invoke: (...groups: (string | undefined)[]) => unknown
): string =>
  expression.replace(pattern, (...args) => {
    const groups = args.slice(1, -2) as (string | undefined)[]
    return serializeFoldedValue(invoke(...groups))
  })

export const foldFormRuleExpression = (
  expression: string,
  data: Record<string, unknown>,
  helpers?: FormRuleExpressionHelpers
): string => {
  let folded = foldLegacyGroupReduce(expression, data)
  folded = foldGroupByNameCalls(folded, data)

  if (!helpers) return folded

  folded = foldNullaryHelperCalls(folded, helpers)

  folded = foldHelperCall(
    folded,
    new RegExp(
      `fieldHasValue\\(\\s*${STRING_ARG}(?:\\s*,\\s*${STRING_ARG})?\\s*\\)`,
      'g'
    ),
    (singleKey, doubleKey, singlePath, doublePath) =>
      helpers.fieldHasValue?.(
        readCapturedString(singleKey, doubleKey),
        singlePath != null || doublePath != null
          ? readCapturedString(singlePath, doublePath)
          : undefined
      ) ?? false
  )

  folded = foldHelperCall(
    folded,
    new RegExp(
      `resolveField\\(\\s*${STRING_ARG}(?:\\s*,\\s*${STRING_ARG})?\\s*\\)`,
      'g'
    ),
    (singleKey, doubleKey, singlePath, doublePath) =>
      helpers.resolveField?.(
        readCapturedString(singleKey, doubleKey),
        singlePath != null || doublePath != null
          ? readCapturedString(singlePath, doublePath)
          : undefined
      )
  )

  folded = foldHelperCall(
    folded,
    new RegExp(
      `resolveCacheProperty\\(\\s*${STRING_ARG}\\s*,\\s*${STRING_ARG}\\s*,\\s*${STRING_ARG}\\s*\\)`,
      'g'
    ),
    (
      singleCache,
      doubleCache,
      singleForm,
      doubleForm,
      singleProperty,
      doubleProperty
    ) =>
      helpers.resolveCacheProperty?.(
        readCapturedString(singleCache, doubleCache),
        readCapturedString(singleForm, doubleForm),
        readCapturedString(singleProperty, doubleProperty)
      )
  )

  folded = foldHelperCall(
    folded,
    new RegExp(
      `ancestorFieldHasValue\\(\\s*${STRING_ARG}\\s*,\\s*${STRING_ARG}(?:\\s*,\\s*${STRING_ARG})?\\s*\\)`,
      'g'
    ),
    (
      singleEntity,
      doubleEntity,
      singleKey,
      doubleKey,
      singlePath,
      doublePath
    ) =>
      helpers.ancestorFieldHasValue?.(
        readCapturedString(singleEntity, doubleEntity),
        readCapturedString(singleKey, doubleKey),
        singlePath != null || doublePath != null
          ? readCapturedString(singlePath, doublePath)
          : undefined
      ) ?? false
  )

  folded = foldHelperCall(
    folded,
    new RegExp(
      `resolveAncestorField\\(\\s*${STRING_ARG}\\s*,\\s*${STRING_ARG}(?:\\s*,\\s*${STRING_ARG})?\\s*\\)`,
      'g'
    ),
    (
      singleEntity,
      doubleEntity,
      singleKey,
      doubleKey,
      singlePath,
      doublePath
    ) =>
      helpers.resolveAncestorField?.(
        readCapturedString(singleEntity, doubleEntity),
        readCapturedString(singleKey, doubleKey),
        singlePath != null || doublePath != null
          ? readCapturedString(singlePath, doublePath)
          : undefined
      )
  )

  folded = foldHelperCall(
    folded,
    new RegExp(
      `parentFieldHasValue\\(\\s*${STRING_ARG}(?:\\s*,\\s*${STRING_ARG})?\\s*\\)`,
      'g'
    ),
    (singleKey, doubleKey, singlePath, doublePath) =>
      helpers.parentFieldHasValue?.(
        readCapturedString(singleKey, doubleKey),
        singlePath != null || doublePath != null
          ? readCapturedString(singlePath, doublePath)
          : undefined
      ) ?? false
  )

  folded = foldHelperCall(
    folded,
    new RegExp(
      `resolveParentField\\(\\s*${STRING_ARG}(?:\\s*,\\s*${STRING_ARG})?\\s*\\)`,
      'g'
    ),
    (singleKey, doubleKey, singlePath, doublePath) =>
      helpers.resolveParentField?.(
        readCapturedString(singleKey, doubleKey),
        singlePath != null || doublePath != null
          ? readCapturedString(singlePath, doublePath)
          : undefined
      )
  )

  folded = foldHelperCall(
    folded,
    new RegExp(
      `resolveAncestorCache\\(\\s*${STRING_ARG}\\s*,\\s*${STRING_ARG}\\s*,\\s*${STRING_ARG}\\s*\\)`,
      'g'
    ),
    (
      singleEntity,
      doubleEntity,
      singleCache,
      doubleCache,
      singlePath,
      doublePath
    ) =>
      helpers.resolveAncestorCache?.(
        readCapturedString(singleEntity, doubleEntity),
        readCapturedString(singleCache, doubleCache),
        readCapturedString(singlePath, doublePath)
      )
  )

  return folded
}
